import { useGetDietaryRequirement } from "@/hooks/requests/dietary-requirement/use-get-dietary-requirement";
import { useGetDietaryRequirementOptions } from "@/hooks/requests/dietary-requirement/use-get-dietary-requirement-options";
import { useEffect, useMemo, useState } from "react";

const LIFESTYLE_OPTIONS_SET = new Set([
  "vegetarian",
  "vegan",
  "pescatarian",
  "flexitarian",
  "halal",
  "kosher",
]);

const MILK_PREFERENCE_OPTIONS_SET = new Set([
  "semi-skimmed",
  "skimmed",
  "soya",
  "oat",
  "almond",
  "coconut",
]);

/**
 * Hook to manage lifestyle dietary options and their selection state.
 * Filters out milk preferences and allergens to show only lifestyle-related dietary choices.
 *
 * @returns Object containing lifestyle options, loading state, and selected options
 */
export const useLifestyleOptions = () => {
  const { dietaryOptions, isLoading: isDietaryOptionsLoading } =
    useGetDietaryRequirementOptions();

  const { dietaryRequirement, isLoading: isDietaryRequirementLoading } =
    useGetDietaryRequirement();

  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set(),
  );

  /**
   * Filter dietary options to extract only lifestyle-related choices.
   * Excludes milk preferences and allergen-related options.
   */
  const lifestyleOptions = useMemo(
    () =>
      dietaryOptions?.filter(
        (option) =>
          LIFESTYLE_OPTIONS_SET.has(option) ||
          (!MILK_PREFERENCE_OPTIONS_SET.has(option) &&
            !option.includes("allergy") &&
            !option.includes("intolerant")),
      ) ?? [],
    [dietaryOptions],
  );

  /**
   * Populate selected lifestyle options when data is fetched.
   * This ensures the UI reflects the user's existing lifestyle preferences.
   */
  useEffect(() => {
    if (dietaryRequirement && lifestyleOptions.length > 0) {
      const selectedLifestyleOptions = lifestyleOptions.filter((option) =>
        dietaryRequirement.dietaryOptions?.includes(option),
      );
      setSelectedOptions(new Set(selectedLifestyleOptions));
    }
  }, [dietaryRequirement, lifestyleOptions]);

  return {
    options: lifestyleOptions,
    isLoading: isDietaryOptionsLoading || isDietaryRequirementLoading,
    selectedOptions,
    setSelectedOptions,
  };
};
